import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Video, Clock, Hash, FileText, ChevronDown, ChevronUp, Play, Trash2, Youtube, HardDriveDownload, AlertCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const getApiBase = () => {
  if (typeof window === 'undefined') return process.env.REACT_APP_BACKEND_URL || '';
  const configured = process.env.REACT_APP_BACKEND_URL || '';
  const origin = window.location.origin;
  if (configured && !configured.startsWith(origin)) return origin;
  return configured || origin;
};
const BACKEND_URL = getApiBase();

export default function HistoryPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [playingId, setPlayingId] = useState(null);

  const loadVideos = useCallback(async () => {
    try {
      const res = await api.get('/videos');
      setVideos(res.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVideos(); }, [loadVideos]);

  // Auto-refresh every 5s while there are videos in "generating" status
  useEffect(() => {
    const hasGenerating = videos.some((v) => v.status === 'generating');
    if (!hasGenerating) return;
    const interval = setInterval(() => loadVideos(), 5000);
    return () => clearInterval(interval);
  }, [videos, loadVideos]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Days remaining before auto-cleanup (14 days from published_at)
  const daysUntilCleanup = (video) => {
    if (!video.published_at || video.file_deleted_at) return null;
    const RETENTION_DAYS = 14;
    const publishedMs = new Date(video.published_at).getTime();
    const cleanupMs = publishedMs + RETENTION_DAYS * 24 * 60 * 60 * 1000;
    const remaining = Math.ceil((cleanupMs - Date.now()) / (24 * 60 * 60 * 1000));
    return remaining > 0 ? remaining : 0;
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Supprimer definitivement "${video.title}" ?\n\nCela supprimera aussi le fichier video.`)) return;
    try {
      await api.delete(`/videos/${video.id}`);
      toast.success('Video supprimee');
      setVideos((v) => v.filter((x) => x.id !== video.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleDeleteFile = async (video) => {
    if (!window.confirm(`Supprimer le fichier video de "${video.title}" ?\n\nL'historique (script, titre, lien YouTube) sera conserve. Utile pour liberer de l'espace.`)) return;
    try {
      await api.delete(`/videos/${video.id}/file`);
      toast.success('Fichier supprime, historique conserve');
      setVideos((v) => v.map((x) => x.id === video.id ? { ...x, video_url: null, audio_url: null, file_deleted_at: new Date().toISOString() } : x));
      setPlayingId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownload = async (video) => {
    try {
      const url = `${BACKEND_URL}/api/videos/${video.id}/stream`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(video.title || 'viralsynth-video').replace(/[^a-z0-9-_ ]/gi, '_').slice(0, 60)}.mp4`;
      link.click();
      toast.success('Telechargement lance');
    } catch {
      toast.error('Erreur');
    }
  };

  const handleRetry = async (video) => {
    try {
      await api.post(`/videos/${video.id}/retry`);
      toast.success('Generation relancee. Patientez quelques secondes...');
      setVideos((vs) =>
        vs.map((x) =>
          x.id === video.id
            ? { ...x, status: 'generating', error_message: null, title: `[En cours] ${x.project_name || 'Video'}` }
            : x
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la relance');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="history-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-white tracking-tight">
            Historique des videos
          </h1>
          <p className="text-sm text-white/40 mt-1">{videos.length} video(s) generee(s)</p>
        </div>
        <div className="hidden sm:flex items-start gap-2 p-3 rounded-lg bg-[#FFD700]/5 border border-[#FFD700]/20 max-w-xs">
          <AlertCircle className="w-4 h-4 text-[#FFD700] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-white/50 leading-relaxed">
            Les fichiers videos publies sont <span className="text-white">auto-supprimes apres 14 jours</span> pour liberer l'espace. L'historique et le lien YouTube sont conserves. Vous pouvez desactiver l'auto-suppression dans les parametres du projet.
          </p>
        </div>
      </div>

      {!videos.length ? (
        <div className="card-surface rounded-lg p-12 text-center">
          <Video className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm">Aucune video generee pour le moment</p>
          <p className="text-white/20 text-xs mt-2">Creez un projet et cliquez sur "Generer" pour commencer</p>
        </div>
      ) : (
        <div className="space-y-3">
          {videos.map((video) => {
            const isExpanded = expandedId === video.id;
            const isPlaying = playingId === video.id;
            const hasFile = !!video.video_url && !video.file_deleted_at;
            return (
              <div key={video.id} className="card-surface rounded-lg overflow-hidden" data-testid={`video-item-${video.id}`}>
                <div
                  className="flex items-center gap-4 p-5 cursor-pointer hover:bg-white/[0.02] transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : video.id)}
                >
                  <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 text-white/20" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{video.title}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-white/30">{video.project_name || 'Projet'}</span>
                      <span className="text-xs text-white/20">{formatDate(video.generated_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {video.generation_type === 'heygen' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/20">Avatar</span>
                    )}
                    {video.youtube_video_id && (
                      <a href={`https://www.youtube.com/watch?v=${video.youtube_video_id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-red-400 hover:underline" onClick={e => e.stopPropagation()} data-testid={`yt-link-${video.id}`}>
                        <Youtube className="w-3 h-3" /> YouTube
                      </a>
                    )}
                    {hasFile && video.status === 'published' && daysUntilCleanup(video) !== null && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          daysUntilCleanup(video) <= 3
                            ? 'bg-orange-500/15 text-orange-400 border-orange-500/30'
                            : 'bg-white/5 text-white/40 border-white/10'
                        }`}
                        title="Le fichier sera auto-supprime apres cette duree (l'historique reste conserve)"
                        data-testid={`retention-${video.id}`}
                      >
                        {daysUntilCleanup(video) === 0
                          ? 'Auto-suppr. imminente'
                          : `${daysUntilCleanup(video)}j restants`}
                      </span>
                    )}
                    <span className={`text-xs px-2.5 py-1 rounded-full ${
                      video.status === 'completed' ? 'badge-success' :
                      video.status === 'published' ? 'badge-success' :
                      video.status === 'failed' ? 'badge-error' : 'badge-pending'
                    }`}>
                      {video.status === 'completed' ? 'Termine' :
                       video.status === 'failed' ? 'Echoue' :
                       video.status === 'published' ? 'Publie' :
                       video.status === 'generating' ? 'Generation...' : 'En cours'}
                    </span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-5 pb-5 border-t border-white/5 pt-4 space-y-4">
                    {/* Failure banner */}
                    {video.status === 'failed' && video.error_message && (
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-red-300">
                          <p className="font-medium mb-0.5">Generation echouee</p>
                          <p className="text-red-300/80">{video.error_message}</p>
                        </div>
                      </div>
                    )}
                    {/* Video player */}
                    {hasFile ? (
                      <div className="rounded-lg overflow-hidden bg-black">
                        {isPlaying ? (
                          <video
                            src={`${BACKEND_URL}/api/videos/${video.id}/stream`}
                            controls
                            autoPlay
                            className="w-full max-h-[480px]"
                            data-testid={`video-player-${video.id}`}
                          />
                        ) : (
                          <button
                            onClick={() => setPlayingId(video.id)}
                            className="w-full aspect-video bg-gradient-to-br from-white/5 to-white/[0.02] flex items-center justify-center hover:from-white/10 hover:to-white/[0.03] transition-all group"
                            data-testid={`play-btn-${video.id}`}
                          >
                            <div className="w-16 h-16 rounded-full bg-[#FF3300]/20 border border-[#FF3300]/40 flex items-center justify-center group-hover:scale-110 group-hover:bg-[#FF3300]/30 transition-all">
                              <Play className="w-8 h-8 text-[#FF3300] ml-1" fill="currentColor" />
                            </div>
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="p-4 rounded-lg bg-white/[0.02] border border-dashed border-white/10 text-center">
                        <HardDriveDownload className="w-6 h-6 text-white/20 mx-auto mb-2" />
                        <p className="text-sm text-white/50">Fichier video supprime</p>
                        <p className="text-xs text-white/30 mt-1">
                          {video.file_deleted_at
                            ? `Supprime le ${formatDate(video.file_deleted_at)} pour liberer l'espace`
                            : 'Fichier indisponible'}
                          {video.youtube_video_id && ' · Toujours accessible sur YouTube'}
                        </p>
                      </div>
                    )}

                    {video.script && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-white/30" />
                          <span className="text-xs text-white/40 uppercase tracking-wider">Script</span>
                        </div>
                        <p className="text-sm text-white/60 leading-relaxed bg-white/[0.02] rounded-lg p-4 max-h-40 overflow-y-auto">
                          {video.script}
                        </p>
                      </div>
                    )}
                    {video.description && (
                      <div>
                        <span className="text-xs text-white/40 uppercase tracking-wider">Description</span>
                        <p className="text-sm text-white/50 mt-1">{video.description}</p>
                      </div>
                    )}
                    {video.hashtags?.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Hash className="w-4 h-4 text-white/30" />
                        {video.hashtags.map((tag) => (
                          <span key={`${video.id}-${tag}`} className="text-xs px-2 py-1 rounded-full bg-white/5 text-white/40">{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-white/5">
                      <div className="flex items-center gap-4 text-xs text-white/30">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(video.generated_at)}</span>
                        {video.published_at && <span>Publie: {formatDate(video.published_at)}</span>}
                        <span>{video.credits_consumed} credits</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {video.status === 'failed' && (
                          <button
                            onClick={() => handleRetry(video)}
                            className="text-xs px-3 py-1.5 rounded-lg bg-[#FF3300]/15 hover:bg-[#FF3300]/25 text-[#FF6633] border border-[#FF3300]/30 transition-colors flex items-center gap-1.5"
                            data-testid={`retry-btn-${video.id}`}
                            title="Relancer la generation"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Relancer
                          </button>
                        )}
                        {hasFile && (
                          <>
                            <button
                              onClick={() => handleDownload(video)}
                              className="text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 transition-colors flex items-center gap-1.5"
                              data-testid={`download-btn-${video.id}`}
                              title="Telecharger le fichier mp4"
                            >
                              <HardDriveDownload className="w-3.5 h-3.5" /> Telecharger
                            </button>
                            <button
                              onClick={() => handleDeleteFile(video)}
                              className="text-xs px-3 py-1.5 rounded-lg bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 transition-colors flex items-center gap-1.5"
                              data-testid={`delete-file-btn-${video.id}`}
                              title="Liberer l'espace (garde l'historique)"
                            >
                              <HardDriveDownload className="w-3.5 h-3.5" /> Liberer espace
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(video)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors flex items-center gap-1.5"
                          data-testid={`delete-btn-${video.id}`}
                          title="Suppression definitive"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
